import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CheckCircle,
  CircleNotch,
  FloppyDisk,
  MagicWand,
  NotePencil,
  PaperPlaneTilt,
  Robot,
  Trash,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@alfred/convex/_generated/api";
import type { Id } from "@alfred/convex/_generated/dataModel";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { cn, relativeTime } from "~/lib/utils";

export const Route = createFileRoute("/tickets/new")({
  component: NewTicketPage,
});

const ISSUE_TYPES = ["Story", "Task"] as const;

/** Editable shape — list fields are edited as one-per-line text. */
interface EditorState {
  summary: string;
  description: string;
  acceptanceCriteria: string;
  outOfScope: string;
  technicalNotes: string;
  openQuestions: string;
}

const toLines = (items: string[] | undefined) => (items ?? []).join("\n");
const fromLines = (text: string) =>
  text
    .split("\n")
    .map((l) => l.trim().replace(/^[-*]\s+/, ""))
    .filter(Boolean);

function NewTicketPage() {
  const drafts = useQuery(api.ticketDrafts.list, { limit: 10 });

  const createDraft = useMutation(api.ticketDrafts.create);
  const redraft = useMutation(api.ticketDrafts.redraft);
  const saveDraft = useMutation(api.ticketDrafts.saveDraft);
  const requestCreate = useMutation(api.ticketDrafts.requestCreate);
  const removeDraft = useMutation(api.ticketDrafts.remove);

  const [selectedId, setSelectedId] = React.useState<Id<"ticketDrafts"> | null>(
    null,
  );
  const selected = useQuery(
    api.ticketDrafts.get,
    selectedId ? { id: selectedId } : "skip",
  );

  // ---- Ask form -----------------------------------------------------------
  const [request, setRequest] = React.useState("");
  const [issueType, setIssueType] =
    React.useState<(typeof ISSUE_TYPES)[number]>("Story");
  const [handToAlfred, setHandToAlfred] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  // ---- Draft editor -------------------------------------------------------
  const [editor, setEditor] = React.useState<EditorState | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [feedback, setFeedback] = React.useState("");

  // Pull the drafted content into the editor whenever a new draft lands (and
  // never clobber in-progress edits).
  const draftedAt = selected?.draftedAt;
  React.useEffect(() => {
    if (!selected?.draft) {
      setEditor(null);
      setDirty(false);
      return;
    }
    if (dirty) return;
    setEditor({
      summary: selected.draft.summary,
      description: selected.draft.description,
      acceptanceCriteria: toLines(selected.draft.acceptanceCriteria),
      outOfScope: toLines(selected.draft.outOfScope),
      technicalNotes: selected.draft.technicalNotes ?? "",
      openQuestions: toLines(selected.draft.openQuestions),
    });
  }, [selectedId, draftedAt, selected?.draft === undefined]);

  const status = selected?.status;
  const working = status === "pending" || status === "drafting";
  const creating = status === "create_requested" || status === "creating";
  const created = status === "created";

  function packDraft() {
    if (!editor) return null;
    return {
      summary: editor.summary.trim(),
      description: editor.description.trim(),
      acceptanceCriteria: fromLines(editor.acceptanceCriteria),
      outOfScope: fromLines(editor.outOfScope),
      technicalNotes: editor.technicalNotes.trim() || undefined,
      openQuestions: fromLines(editor.openQuestions),
      repos: selected?.draft?.repos ?? [],
      labels: selected?.draft?.labels ?? [],
    };
  }

  async function onSubmitAsk() {
    if (!request.trim()) {
      toast.error("Describe what the ticket should cover.");
      return;
    }
    setSubmitting(true);
    try {
      const id = (await createDraft({
        request,
        issueType,
        handToAlfred,
      })) as Id<"ticketDrafts">;
      setSelectedId(id);
      setDirty(false);
      setRequest("");
      toast.success("Alfred is drafting", {
        description: "Reading the brief and its memory — about a minute.",
      });
    } catch (err) {
      toast.error(`Couldn't queue the draft: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function onSave() {
    const draft = packDraft();
    if (!selectedId || !draft) return;
    setBusy(true);
    try {
      await saveDraft({ id: selectedId, draft });
      setDirty(false);
      toast.success("Edits saved");
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function onRedraft() {
    if (!selectedId) return;
    setBusy(true);
    try {
      await redraft({ id: selectedId, feedback: feedback || undefined });
      setFeedback("");
      setDirty(false);
      toast.success("Re-drafting", {
        description: feedback
          ? "Alfred will revise with your steer."
          : "Alfred will take another pass.",
      });
    } catch (err) {
      toast.error(`Re-draft failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function onCreate() {
    const draft = packDraft();
    if (!selectedId || !draft) return;
    if (!draft.summary) {
      toast.error("Summary can't be empty.");
      return;
    }
    if (draft.acceptanceCriteria.length === 0) {
      toast.error("Add at least one acceptance criterion.");
      return;
    }
    setBusy(true);
    try {
      await requestCreate({ id: selectedId, draft });
      setDirty(false);
      toast.success("Creating in Jira…");
    } catch (err) {
      toast.error(`Create failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function patch(next: Partial<EditorState>) {
    setEditor((cur) => (cur ? { ...cur, ...next } : cur));
    setDirty(true);
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-border/70 bg-background/85 px-4 py-4 backdrop-blur lg:px-6">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <NotePencil size={18} weight="duotone" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">New Ticket</h1>
            <p className="text-xs text-muted-foreground">
              Describe what you want in a sentence or two. Alfred finds the
              relevant product and repo knowledge itself, writes the ticket
              from it, and you review before anything reaches Jira.
            </p>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 lg:px-6">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          {/* ---- Ask + history ------------------------------------------ */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  What do you need?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <textarea
                  value={request}
                  onChange={(e) => setRequest(e.target.value)}
                  rows={5}
                  placeholder="e.g. Let recruiters send a referral over SMS, not just email"
                  className="w-full resize-y rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary/60"
                />

                <div>
                  <p className="mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Type
                  </p>
                  <div className="inline-flex rounded-xl border border-border bg-card p-0.5">
                    {ISSUE_TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setIssueType(t)}
                        className={cn(
                          "rounded-lg px-3 py-1 text-xs font-medium transition-colors",
                          issueType === t
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-border bg-card p-2.5 text-xs">
                  <input
                    type="checkbox"
                    checked={handToAlfred}
                    onChange={(e) => setHandToAlfred(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">Hand to Alfred</span>
                    <span className="block text-muted-foreground">
                      Adds the <code>alfred</code> label, so Alfred imports the
                      ticket and starts working it automatically.
                    </span>
                  </span>
                </label>

                <Button
                  className="w-full"
                  onClick={onSubmitAsk}
                  disabled={submitting || !request.trim()}
                >
                  {submitting ? (
                    <CircleNotch size={14} className="animate-spin" />
                  ) : (
                    <MagicWand size={14} weight="bold" />
                  )}
                  Draft with Alfred
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Recent
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {(drafts ?? []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Nothing drafted yet.
                  </p>
                ) : (
                  (drafts ?? []).map((d) => (
                    <button
                      key={d._id}
                      type="button"
                      onClick={() => {
                        setSelectedId(d._id);
                        setDirty(false);
                      }}
                      className={cn(
                        "flex w-full items-start justify-between gap-2 rounded-xl border px-2.5 py-2 text-left text-xs transition-colors",
                        selectedId === d._id
                          ? "border-primary/60 bg-primary/10"
                          : "border-border hover:border-border/80",
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">
                          {d.draft?.summary ?? d.request}
                        </span>
                        <span className="text-muted-foreground">
                          {relativeTime(d.createdAt)}
                        </span>
                      </span>
                      <StatusBadge status={d.status} jiraKey={d.jiraKey} />
                    </button>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* ---- Draft review ------------------------------------------- */}
          <div className="min-w-0">
            {!selected ? (
              <EmptyState />
            ) : created ? (
              <CreatedState
                jiraKey={selected.jiraKey!}
                jiraUrl={selected.jiraUrl!}
                onBoard={selected.onBoard !== false}
                handedToAlfred={Boolean(selected.handToAlfred)}
                onStartAnother={() => {
                  setSelectedId(null);
                  setEditor(null);
                }}
              />
            ) : working ? (
              <PendingState label="Alfred is drafting" request={selected.request} />
            ) : creating ? (
              <PendingState label="Creating in Jira" request={selected.request} />
            ) : status === "failed" ? (
              <FailedState
                error={selected.error ?? "Unknown error"}
                onRetry={onRedraft}
                busy={busy}
              />
            ) : editor ? (
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-3">
                  <div className="min-w-0">
                    <CardTitle className="text-sm">Review the draft</CardTitle>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Everything here is editable. Nothing reaches Jira until
                      you create it.
                    </p>
                  </div>
                  <Grounding grounded={selected.groundedIn} />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Field label="Summary">
                    <input
                      value={editor.summary}
                      onChange={(e) => patch({ summary: e.target.value })}
                      className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary/60"
                    />
                  </Field>

                  <Field label="Description" hint="Markdown">
                    <textarea
                      value={editor.description}
                      onChange={(e) => patch({ description: e.target.value })}
                      rows={10}
                      className="w-full resize-y rounded-xl border border-border bg-card p-3 font-mono text-xs leading-relaxed outline-none focus:border-primary/60"
                    />
                  </Field>

                  <Field
                    label="Acceptance criteria"
                    hint="one per line — rendered as a bulleted section in Jira"
                  >
                    <textarea
                      value={editor.acceptanceCriteria}
                      onChange={(e) =>
                        patch({ acceptanceCriteria: e.target.value })
                      }
                      rows={7}
                      className="w-full resize-y rounded-xl border border-border bg-card p-3 text-xs leading-relaxed outline-none focus:border-primary/60"
                    />
                  </Field>

                  <Field label="Out of scope" hint="one per line">
                    <textarea
                      value={editor.outOfScope}
                      onChange={(e) => patch({ outOfScope: e.target.value })}
                      rows={3}
                      className="w-full resize-y rounded-xl border border-border bg-card p-3 text-xs leading-relaxed outline-none focus:border-primary/60"
                    />
                  </Field>

                  <Field label="Technical notes" hint="from repo memory">
                    <textarea
                      value={editor.technicalNotes}
                      onChange={(e) => patch({ technicalNotes: e.target.value })}
                      rows={4}
                      className="w-full resize-y rounded-xl border border-border bg-card p-3 text-xs leading-relaxed outline-none focus:border-primary/60"
                    />
                  </Field>

                  {editor.openQuestions.trim() ? (
                    <Field
                      label="Open questions"
                      hint="Alfred couldn't resolve these — answer or delete"
                    >
                      <textarea
                        value={editor.openQuestions}
                        onChange={(e) =>
                          patch({ openQuestions: e.target.value })
                        }
                        rows={3}
                        className="w-full resize-y rounded-xl border border-[color:var(--brand-orange)]/40 bg-card p-3 text-xs leading-relaxed outline-none focus:border-primary/60"
                      />
                    </Field>
                  ) : null}

                  <div className="rounded-xl border border-border bg-card/60 p-3">
                    <p className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                      Not right? Tell Alfred what to change
                    </p>
                    <div className="flex gap-2">
                      <input
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="e.g. scope it to the API only, add AC for the empty state"
                        className="min-w-0 flex-1 rounded-xl border border-border bg-card px-3 py-2 text-xs outline-none focus:border-primary/60"
                      />
                      <Button
                        variant="outline"
                        onClick={onRedraft}
                        disabled={busy}
                      >
                        <ArrowsClockwise size={14} weight="bold" />
                        Re-draft
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (!selectedId) return;
                          await removeDraft({ id: selectedId });
                          setSelectedId(null);
                          setEditor(null);
                        }}
                      >
                        <Trash size={14} />
                        Discard
                      </Button>
                      {dirty ? (
                        <span className="text-[11px] text-[color:var(--brand-orange)]">
                          unsaved edits
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={onSave}
                        disabled={!dirty || busy}
                      >
                        <FloppyDisk size={14} weight="bold" />
                        Save
                      </Button>
                      <Button onClick={onCreate} disabled={busy}>
                        {busy ? (
                          <CircleNotch size={14} className="animate-spin" />
                        ) : (
                          <PaperPlaneTilt size={14} weight="bold" />
                        )}
                        Create in Jira
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
        {hint ? (
          <span className="ml-1.5 normal-case tracking-normal opacity-70">
            {hint}
          </span>
        ) : null}
      </p>
      {children}
    </div>
  );
}

function Grounding({
  grounded,
}: {
  grounded?: {
    briefUsed: boolean;
    productMemoryCount: number;
    repoMemoryCount: number;
    repos: string[];
  } | null;
}) {
  if (!grounded) return null;
  return (
    <div className="flex shrink-0 flex-wrap justify-end gap-1">
      {grounded.briefUsed ? <Badge variant="muted">brief</Badge> : null}
      {grounded.productMemoryCount > 0 ? (
        <Badge variant="muted">{grounded.productMemoryCount} product notes</Badge>
      ) : null}
      {grounded.repoMemoryCount > 0 ? (
        <Badge variant="muted">{grounded.repoMemoryCount} repo notes</Badge>
      ) : null}
      {/* Which repos retrieval landed on — the human didn't choose these, so
          showing them is how you sanity-check that it looked in the right
          places. */}
      {grounded.repos.map((r) => (
        <Badge key={r} variant="outline">
          {r.split("/").pop()}
        </Badge>
      ))}
    </div>
  );
}

function StatusBadge({
  status,
  jiraKey,
}: {
  status: string;
  jiraKey?: string;
}) {
  if (status === "created") {
    return <Badge variant="success">{jiraKey ?? "created"}</Badge>;
  }
  if (status === "failed") return <Badge variant="warn">failed</Badge>;
  if (status === "drafted") return <Badge variant="outline">review</Badge>;
  return <Badge variant="muted">{status.replace("_", " ")}</Badge>;
}

function EmptyState() {
  return (
    <Card className="flex h-full min-h-[20rem] items-center justify-center">
      <CardContent className="max-w-sm space-y-2 py-10 text-center">
        <Robot
          size={28}
          weight="duotone"
          className="mx-auto text-muted-foreground"
        />
        <p className="text-sm font-medium">No draft selected</p>
        <p className="text-xs text-muted-foreground">
          Describe what you need on the left. Alfred searches the tech brief
          and everything it has learned about the product and the repos, so
          the acceptance criteria come out specific rather than generic.
        </p>
      </CardContent>
    </Card>
  );
}

function PendingState({
  label,
  request,
}: {
  label: string;
  request: string;
}) {
  return (
    <Card className="flex h-full min-h-[20rem] items-center justify-center">
      <CardContent className="max-w-md space-y-3 py-10 text-center">
        <CircleNotch
          size={26}
          className="mx-auto animate-spin text-muted-foreground"
        />
        <p className="text-sm font-medium">{label}…</p>
        <p className="text-xs text-muted-foreground">“{request}”</p>
        <p className="text-[11px] text-muted-foreground">
          Reading the brief and its memory. This usually takes under a minute —
          the page updates itself.
        </p>
      </CardContent>
    </Card>
  );
}

function FailedState({
  error,
  onRetry,
  busy,
}: {
  error: string;
  onRetry: () => void;
  busy: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Warning size={16} className="text-[color:var(--brand-orange)]" />
          That didn't work
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl border border-border bg-card p-3 text-xs text-muted-foreground">
          {error}
        </pre>
        <Button variant="outline" onClick={onRetry} disabled={busy}>
          <ArrowsClockwise size={14} weight="bold" />
          Try again
        </Button>
      </CardContent>
    </Card>
  );
}

function CreatedState({
  jiraKey,
  jiraUrl,
  onBoard,
  handedToAlfred,
  onStartAnother,
}: {
  jiraKey: string;
  jiraUrl: string;
  onBoard: boolean;
  handedToAlfred: boolean;
  onStartAnother: () => void;
}) {
  return (
    <Card className="flex h-full min-h-[20rem] items-center justify-center">
      <CardContent className="max-w-md space-y-3 py-10 text-center">
        <CheckCircle
          size={30}
          weight="duotone"
          className="mx-auto text-[color:var(--brand-green,#4ade80)]"
        />
        <p className="text-sm font-medium">
          Created {jiraKey}
          {onBoard ? " on the board" : ""}
        </p>
        {onBoard ? null : (
          <p className="text-xs text-[color:var(--brand-amber,#fbbf24)]">
            Couldn't move it onto the board — it's in the backlog and needs
            dragging across.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {handedToAlfred
            ? "Labelled `alfred` — it'll be imported and picked up automatically on the next sync."
            : "Not handed to Alfred. Add the `alfred` label in Jira whenever you want it worked."}
        </p>
        <div className="flex justify-center gap-2 pt-1">
          <a href={jiraUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm">
              <ArrowSquareOut size={14} weight="bold" />
              Open in Jira
            </Button>
          </a>
          <Button size="sm" onClick={onStartAnother}>
            <NotePencil size={14} weight="bold" />
            Draft another
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
